/* eslint-disable react/prop-types -- this codebase does not use propTypes (see CLAUDE.md). */
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { track, trackOnce } from "../../utils/analytics";
import { notifyError } from "../../utils/bugsnag";
import { safeStorage } from "../../utils/storage";
import { useStartData } from "./StartContext";
import { useFlyerJob } from "./FlyerJob";
import * as startApi from "./api";
import { ensureFlyerRecord } from "./setupChain";
import { returnedFromCheckout } from "./checkout";
import { ErrorBox, InfoBox, PrimaryButton, StickyFooter, cx } from "./ui";

// Go live: pick a plan and start the 14-day trial in Stripe Checkout. Prices,
// names and features come from the same pricing.* strings as the /setup
// paywall, and Annual + Growth start selected there too, so the A/B test
// compares the two flows and not two price pages.

const PLANS = ["starter", "growth", "advanced"];
const FEATURED = "growth";
const INTERVALS = ["month", "year"];
const CHECKOUT_STARTED_KEY = "start:checkoutStartedAt";

// Released if the page is somehow still here this long after the redirect
// (an in-app browser that dropped the navigation), so the button is not stuck.
const STUCK_MS = 30000;

// Mirrors Pricing.jsx: the advanced tier's 5th feature is not shipped yet.
const SOON = { advanced: [4] };

// Read when needed rather than at import, so a missing build variable shows
// up as a checkout error instead of a silent undefined product.
function productIds() {
  return {
    starter: import.meta.env.VITE_STARTER_PLAN_ID,
    growth: import.meta.env.VITE_GROWTH_PLAN_ID,
    advanced: import.meta.env.VITE_ADVANCED_PLAN_ID,
  };
}

// Tolgee exports can turn arrays into {"0": ..., "1": ...}; a missing key
// comes back as the key itself.
function listOf(value) {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return Object.values(value);
  return [];
}

function firstNameOf(user) {
  return user?.displayName?.trim().split(/\s+/)[0] || "";
}

// Arrow keys move the selection inside a radio group, as native radios do.
function handleRadioKeys(event, values, current, select) {
  const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
  if (!step) return;
  event.preventDefault();
  const index = values.indexOf(current);
  const next = values[(index + step + values.length) % values.length];
  select(next);
  event.currentTarget
    .closest('[role="radiogroup"]')
    ?.querySelector(`[data-value="${next}"]`)
    ?.focus();
}

function CheckMark({ className }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 20 20" fill="currentColor" className={className}>
      <path
        fillRule="evenodd"
        d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function IntervalToggle({ interval, onChange }) {
  const { t } = useLanguage();
  return (
    <div
      role="radiogroup"
      aria-label={t("start.plan.billingLabel")}
      className="grid grid-cols-2 gap-1 rounded-xl bg-gray-100 p-1"
    >
      {INTERVALS.map((value) => {
        const checked = interval === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={checked ? 0 : -1}
            data-value={value}
            onClick={() => onChange(value)}
            onKeyDown={(event) => handleRadioKeys(event, INTERVALS, interval, onChange)}
            className={cx(
              "flex min-h-12 flex-col items-center justify-center rounded-lg px-2 py-1 text-sm font-semibold transition",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4522ED]",
              checked ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900",
            )}
          >
            {value === "month" ? (
              t("pricing.monthly")
            ) : (
              <>
                <span>{t("pricing.annual")}</span>
                <span className="text-xs font-semibold text-green-700">{t("pricing.annualDiscount")}</span>
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}

function PlanOption({ plan, checked, annual, onSelect, onKeyDown }) {
  const { t } = useLanguage();
  const id = useId();
  const features = listOf(t(`pricing.tier.${plan}.features`));
  // Entries 1 and 2 are the card-design and branch limits in every tier, which
  // is what actually differs between them at a glance.
  const highlights = features.slice(1, 3).filter(Boolean);
  const price = t(`pricing.tier.${plan}.${annual ? "priceYearly" : "priceMonthly"}`);

  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      aria-labelledby={`${id}-name`}
      aria-describedby={`${id}-details`}
      tabIndex={checked ? 0 : -1}
      data-value={plan}
      onClick={() => onSelect(plan)}
      onKeyDown={onKeyDown}
      className={cx(
        "flex w-full items-start gap-3 rounded-2xl border bg-white p-4 text-left transition",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4522ED]",
        checked ? "border-[#4522ED] ring-1 ring-[#4522ED]" : "border-gray-200 hover:border-gray-300",
      )}
    >
      <span
        aria-hidden="true"
        className={cx(
          "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2",
          checked ? "border-[#4522ED]" : "border-gray-300",
        )}
      >
        {checked && <span className="h-2.5 w-2.5 rounded-full bg-[#4522ED]" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span id={`${id}-name`} className="text-base font-semibold text-gray-900">
            {t(`pricing.tier.${plan}.name`)}
          </span>
          {plan === FEATURED && (
            <span className="rounded-full bg-[#EEEBFE] px-2 py-0.5 text-xs font-semibold text-[#2F12B8]">
              {t("pricing.mostPopular")}
            </span>
          )}
        </span>
        <span id={`${id}-details`} className="mt-1 block text-sm text-gray-600">
          {highlights.join(" · ")}
          <span className="sr-only">
            {` · €${price} / ${annual ? t("pricing.year") : t("pricing.month")}`}
          </span>
        </span>
      </span>
      <span aria-hidden="true" className="max-w-[7.5rem] shrink-0 text-right">
        <span className="block text-2xl font-bold leading-tight tracking-tight text-gray-900">€{price}</span>
        <span className="block text-xs leading-snug text-gray-500">
          / {annual ? t("pricing.year") : t("pricing.month")}
        </span>
      </span>
    </button>
  );
}

function Included({ plan }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const id = useId();
  const features = listOf(t(`pricing.tier.${plan}.features`));
  const soon = SOON[plan] || [];

  return (
    <div className="rounded-2xl border border-gray-200 bg-white">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((value) => !value)}
        className="flex min-h-12 w-full items-center justify-between gap-3 rounded-2xl px-4 text-left text-sm font-semibold text-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4522ED]"
      >
        {t("start.plan.included", { plan: t(`pricing.tier.${plan}.name`) })}
        <svg
          aria-hidden="true"
          viewBox="0 0 20 20"
          fill="currentColor"
          className={cx("h-5 w-5 shrink-0 text-gray-500 transition", open && "rotate-180")}
        >
          <path
            fillRule="evenodd"
            d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {open && (
        <div id={id} className="border-t border-gray-100 px-4 pb-4 pt-3">
          <p className="text-sm text-gray-600">{t(`pricing.tier.${plan}.description`)}</p>
          <ul className="mt-3 space-y-2 text-sm text-gray-700">
            {features.map((feature, index) => (
              <li key={index} className="flex gap-2.5">
                {soon.includes(index) ? (
                  <span aria-hidden="true" className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300" />
                ) : (
                  <CheckMark className="mt-0.5 h-4 w-4 shrink-0 text-[#4522ED]" />
                )}
                <span className={soon.includes(index) ? "text-gray-400" : undefined}>{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function GoLive() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const { storeId, initForm, flyer, refetchStatus } = useStartData();
  const { colors } = useFlyerJob();

  const [plan, setPlan] = useState(FEATURED);
  const [interval, setBillingInterval] = useState("year");
  const [submitting, setSubmitting] = useState(false);
  const [failed, setFailed] = useState(false);
  // A ref, not state: two taps in the same frame both see submitting=false.
  const lock = useRef(false);
  const stuckTimer = useRef(null);
  const viewTracked = useRef(false);

  const cancelled = searchParams.get("payment") === "cancel";
  const annual = interval === "year";
  const firstName = firstNameOf(user);

  useEffect(() => {
    if (viewTracked.current) return;
    viewTracked.current = true;
    track("start_plan_viewed", { returned_from_checkout: cancelled });
  }, [cancelled]);

  // Keyed by the checkout it came back from, so a reload of the cancel URL
  // does not count the same abandoned checkout twice.
  useEffect(() => {
    if (!cancelled) return;
    const startedAt = safeStorage.get(CHECKOUT_STARTED_KEY);
    trackOnce(`start_checkout_cancel:${startedAt || "unknown"}`, "start_checkout_cancel_returned", {
      seconds_in_checkout: startedAt ? Math.round((Date.now() - Number(startedAt)) / 1000) : null,
    });
  }, [cancelled]);

  const release = useCallback(() => {
    lock.current = false;
    setSubmitting(false);
    clearTimeout(stuckTimer.current);
  }, []);

  // The back button from Stripe restores this page from the back/forward cache
  // exactly as it was left: busy and locked. Unlock it, and re-read the status
  // in case the subscription went through in the meantime (StepGate then moves
  // on to Done).
  useEffect(() => {
    const onPageShow = (event) => {
      if (!event.persisted) return;
      release();
      Promise.resolve()
        .then(() => refetchStatus?.())
        .catch(() => {});
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [release, refetchStatus]);

  useEffect(() => () => clearTimeout(stuckTimer.current), []);

  const choosePlan = (value) => {
    if (value === plan) return;
    setPlan(value);
    track("start_plan_selected", { plan: value, interval });
  };

  const chooseInterval = (value) => {
    if (value === interval) return;
    setBillingInterval(value);
    track("start_plan_selected", { plan, interval: value });
  };

  const startCheckout = async () => {
    if (lock.current) return;
    // A page restored from the back/forward cache after paying skips the
    // route gate: never open a second checkout (and a second trial).
    if (returnedFromCheckout("")) {
      navigate("/start/done", { replace: true });
      return;
    }
    lock.current = true;
    setSubmitting(true);
    setFailed(false);
    const chosen = { plan, interval };
    track("start_checkout_clicked", chosen);

    let stage = "flyer";
    try {
      // check_setup only reports setup complete once a flyer exists, and the
      // Stripe webhook does not create one. Its PDF can follow later.
      if (!flyer) {
        if (!initForm?.id) throw new Error("No sign-up form to make a flyer for");
        await ensureFlyerRecord({ storeId, formId: initForm.id, paperSize: "a4", colors, t });
      }
      stage = "checkout";
      const productId = productIds()[plan];
      if (!storeId || !productId) throw new Error(`No Stripe product configured for ${plan}`);
      const data = await startApi.createCheckout(storeId, productId, interval);
      if (!data?.checkout_url) throw new Error("Checkout response without checkout_url");
      safeStorage.set(CHECKOUT_STARTED_KEY, Date.now());
      // Stays busy until the browser leaves for Stripe.
      clearTimeout(stuckTimer.current);
      stuckTimer.current = setTimeout(release, STUCK_MS);
      window.location.assign(data.checkout_url);
    } catch (error) {
      release();
      setFailed(true);
      track("start_checkout_failed", { ...chosen, stage, status: error?.response?.status || null });
      try {
        notifyError(error, (event) => {
          event.context = `start.checkout.${stage}`;
        });
      } catch {
        // reporting must never break the screen
      }
    }
  };

  return (
    <div className="space-y-5">
      {cancelled && (
        <div role="status">
          <InfoBox tone="gray">{t("start.plan.cancelled")}</InfoBox>
        </div>
      )}

      <div>
        <p className="inline-flex items-center gap-1.5 rounded-full border border-green-200 bg-green-50 px-3 py-1 text-xs font-semibold text-green-800">
          <CheckMark className="h-4 w-4" />
          {t("start.plan.readyBadge")}
        </p>
        <h1 className="mt-3 text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
          {firstName ? t("start.plan.title", { name: firstName }) : t("start.plan.titleNoName")}
        </h1>
        <p className="mt-1 text-base text-gray-600">{t("start.plan.subtitle")}</p>
      </div>

      <InfoBox tone="green">{t("start.plan.notFinal")}</InfoBox>

      <div className="space-y-3">
        <IntervalToggle interval={interval} onChange={chooseInterval} />
        <div role="radiogroup" aria-label={t("start.plan.planLabel")} className="space-y-3">
          {PLANS.map((value) => (
            <PlanOption
              key={value}
              plan={value}
              checked={plan === value}
              annual={annual}
              onSelect={choosePlan}
              onKeyDown={(event) => handleRadioKeys(event, PLANS, plan, choosePlan)}
            />
          ))}
        </div>
        <Included plan={plan} />
      </div>

      <StickyFooter>
        {failed && (
          <div className="mb-3">
            <ErrorBox onRetry={startCheckout} retryLabel={t("start.plan.retry")}>
              {t("start.plan.error")}
            </ErrorBox>
          </div>
        )}
        <p className="mb-2 text-center text-sm font-medium text-gray-700">{t("start.plan.todayLine")}</p>
        <PrimaryButton onClick={startCheckout} loading={submitting}>
          {submitting ? t("start.plan.redirecting") : t("start.plan.cta")}
        </PrimaryButton>
        <p className="sr-only" aria-live="polite">
          {submitting ? t("start.plan.redirecting") : ""}
        </p>
      </StickyFooter>
    </div>
  );
}
